/**
 * Frontend security utilities
 */

// Content Security Policy helpers
export const CSPUtils = {
  /**
   * Generate nonce for inline scripts/styles
   */
  generateNonce: () => {
    const array = new Uint8Array(16)
    crypto.getRandomValues(array)
    return btoa(String.fromCharCode(...array))
  },

  /**
   * Validate external URLs before navigation
   */
  isValidExternalUrl: (url) => {
    try {
      const urlObj = new URL(url)
      const allowedDomains = [
        'google.com',
        'googleapis.com',
        'accounts.google.com',
        'mybusiness.google.com'
      ]
      
      return allowedDomains.some(domain => 
        urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
      )
    } catch {
      return false
    }
  }
}

// Input validation and sanitization
export const InputValidator = {
  /**
   * Validate email format
   */
  validateEmail: (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email) && email.length <= 254
  },

  /**
   * Validate password strength
   */
  validatePassword: (password) => {
    const result = {
      valid: true,
      errors: [],
      score: 0
    }

    if (password.length < 8) {
      result.valid = false
      result.errors.push('Password must be at least 8 characters long')
    } else if (password.length >= 12) {
      result.score += 2
    } else {
      result.score += 1
    }

    if (!/[A-Z]/.test(password)) {
      result.errors.push('Password should contain at least one uppercase letter')
    } else {
      result.score += 1
    }

    if (!/[a-z]/.test(password)) {
      result.errors.push('Password should contain at least one lowercase letter')
    } else {
      result.score += 1
    }

    if (!/\d/.test(password)) {
      result.errors.push('Password should contain at least one digit')
    } else {
      result.score += 1
    }

    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      result.errors.push('Password should contain at least one special character')
    } else {
      result.score += 1
    }

    // Check for common patterns
    const commonPatterns = [
      /123456/,
      /password/i,
      /qwerty/i,
      /abc123/i,
      /admin/i,
      /letmein/i
    ]

    for (const pattern of commonPatterns) {
      if (pattern.test(password)) {
        result.valid = false
        result.errors.push('Password contains common patterns')
        break
      }
    }

    // Check for repeated characters
    if (/(.)\1{2,}/.test(password)) {
      result.errors.push('Password should not contain repeated characters')
    }

    return result
  },

  /**
   * Sanitize string input
   */
  sanitizeString: (input, maxLength = null) => {
    if (typeof input !== 'string') {
      return String(input)
    }

    // Remove null bytes and control characters
    let sanitized = input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')

    // HTML escape
    sanitized = sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')

    // Trim whitespace
    sanitized = sanitized.trim()

    // Apply length limit
    if (maxLength && sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength)
    }

    return sanitized
  },

  /**
   * Validate business name
   */
  validateBusinessName: (name) => {
    if (!name || name.trim().length < 2) {
      return false
    }

    if (name.length > 100) {
      return false
    }

    // Allow letters, numbers, spaces, and common business punctuation
    const pattern = /^[a-zA-Z0-9\s\-\.\&\'\,]+$/
    return pattern.test(name)
  },

  /**
   * Detect potentially malicious content
   */
  detectMaliciousContent: (content) => {
    const suspiciousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe[^>]*>.*?<\/iframe>/gi,
      /data:text\/html/gi,
      /vbscript:/gi
    ]

    return suspiciousPatterns.some(pattern => pattern.test(content))
  }
}

// XSS Protection utilities
export const XSSProtection = {
  /**
   * Escape HTML content
   */
  escapeHtml: (unsafe) => {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  },

  /**
   * Sanitize URL for safe navigation
   */
  sanitizeUrl: (url) => {
    if (!url) return '#'
    
    // Block javascript: and data: URLs
    if (/^(javascript|data|vbscript):/i.test(url)) {
      return '#'
    }

    // Allow only http, https, and relative URLs
    if (!/^(https?:\/\/|\/|#)/.test(url)) {
      return '#'
    }

    return url
  },

  /**
   * Safe innerHTML replacement
   */
  safeInnerHTML: (element, content) => {
    // Create a temporary element to parse content
    const temp = document.createElement('div')
    temp.textContent = content
    element.innerHTML = temp.innerHTML
  }
}

// CSRF Protection
export const CSRFProtection = {
  /**
   * Get CSRF token from meta tag or header
   */
  getToken: () => {
    // Try to get from meta tag first
    const metaToken = document.querySelector('meta[name="csrf-token"]')
    if (metaToken) {
      return metaToken.getAttribute('content')
    }

    // Try to get from localStorage (if set by server)
    return localStorage.getItem('csrf-token')
  },

  /**
   * Set CSRF token
   */
  setToken: (token) => {
    localStorage.setItem('csrf-token', token)
    
    // Also set in meta tag if it exists
    const metaToken = document.querySelector('meta[name="csrf-token"]')
    if (metaToken) {
      metaToken.setAttribute('content', token)
    }
  },

  /**
   * Add CSRF token to request headers
   */
  addToHeaders: (headers = {}) => {
    const token = CSRFProtection.getToken()
    if (token) {
      headers['X-CSRF-Token'] = token
    }
    return headers
  }
}

// Secure storage utilities
export const SecureStorage = {
  /**
   * Store sensitive data with encryption (basic client-side)
   */
  setSecureItem: (key, value) => {
    try {
      // In a real implementation, you'd use proper encryption
      // For now, just use sessionStorage for sensitive data
      const data = {
        value: value,
        timestamp: Date.now(),
        expires: Date.now() + (30 * 60 * 1000) // 30 minutes
      }
      sessionStorage.setItem(key, JSON.stringify(data))
    } catch (error) {
      console.error('Failed to store secure item:', error)
    }
  },

  /**
   * Retrieve and validate stored data
   */
  getSecureItem: (key) => {
    try {
      const stored = sessionStorage.getItem(key)
      if (!stored) return null

      const data = JSON.parse(stored)
      
      // Check if expired
      if (Date.now() > data.expires) {
        sessionStorage.removeItem(key)
        return null
      }

      return data.value
    } catch (error) {
      console.error('Failed to retrieve secure item:', error)
      return null
    }
  },

  /**
   * Clear all secure storage
   */
  clearSecureStorage: () => {
    try {
      // Clear sensitive items from sessionStorage
      const keysToRemove = []
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && (key.includes('token') || key.includes('secure'))) {
          keysToRemove.push(key)
        }
      }
      
      keysToRemove.forEach(key => sessionStorage.removeItem(key))
    } catch (error) {
      console.error('Failed to clear secure storage:', error)
    }
  }
}

// Session security
export const SessionSecurity = {
  /**
   * Generate session fingerprint
   */
  generateFingerprint: () => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillText('Session fingerprint', 2, 2)
    
    const fingerprint = {
      screen: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      canvas: canvas.toDataURL(),
      userAgent: navigator.userAgent.substring(0, 100) // Truncate for storage
    }

    return btoa(JSON.stringify(fingerprint))
  },

  /**
   * Validate session integrity
   */
  validateSession: () => {
    const storedFingerprint = localStorage.getItem('session-fingerprint')
    const currentFingerprint = SessionSecurity.generateFingerprint()
    
    if (!storedFingerprint) {
      localStorage.setItem('session-fingerprint', currentFingerprint)
      return true
    }

    return storedFingerprint === currentFingerprint
  },

  /**
   * Clear session data
   */
  clearSession: () => {
    localStorage.removeItem('session-fingerprint')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    SecureStorage.clearSecureStorage()
  }
}

// Rate limiting for client-side
export const ClientRateLimit = {
  requests: new Map(),

  /**
   * Check if request is allowed
   */
  isAllowed: (endpoint, limit = 10, windowMs = 60000) => {
    const now = Date.now()
    const key = endpoint
    
    if (!ClientRateLimit.requests.has(key)) {
      ClientRateLimit.requests.set(key, [])
    }

    const requests = ClientRateLimit.requests.get(key)
    
    // Remove old requests outside the window
    const validRequests = requests.filter(time => now - time < windowMs)
    ClientRateLimit.requests.set(key, validRequests)

    if (validRequests.length >= limit) {
      return false
    }

    validRequests.push(now)
    return true
  },

  /**
   * Clear rate limit data
   */
  clear: () => {
    ClientRateLimit.requests.clear()
  }
}

// Export all utilities
export default {
  CSPUtils,
  InputValidator,
  XSSProtection,
  CSRFProtection,
  SecureStorage,
  SessionSecurity,
  ClientRateLimit
}